import React, {useEffect, useState} from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    DialogActions,
    Select,
    MenuItem,
    Alert,
    AlertTitle,
    Button,
    FormControl,
    InputLabel,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import {updatePartnerConnection} from '../../../../API/PartnerRevenue';
import {flattenConnection, labelsToStats} from '../connectionDisplay';

function credentialFieldsFor(connection) {
    const defined = connection?.partner?.credential_fields || [];
    const extraKeys = Object.keys(connection?.credentials || {}).filter(
        (key) => !defined.some((field) => field.key === key)
    );

    return [
        ...defined,
        ...extraKeys.map((key) => ({key, label: key})),
    ];
}

const ApiConnectionsTable = ({data, onReload}) => {
    const [rows, setRows] = useState([]);
    const [keys, setKeys] = useState([]);
    const [open, setOpen] = useState(false);
    const [openSend, setOpenSend] = useState(false);
    const [editRow, setEditRow] = useState(null);
    const [form, setForm] = useState({
        name: '',
        base_url: '',
        is_enabled: true,
        credentials: {},
        settings: {},
    });

    useEffect(() => {
        if (!data?.length) {
            setRows([]);
            setKeys([]);
            return;
        }
        const longestItem = data.reduce((prev, current) => (
            Object.keys(prev.value).filter((key) => key !== '_raw').length >
            Object.keys(current.value).filter((key) => key !== '_raw').length
                ? prev
                : current
        ));
        setKeys(Object.keys(longestItem.value).filter((key) => key !== '_raw'));
        setRows(data);
    }, [data]);

    if (!data || data.length === 0) {
        return <div></div>;
    }

    const handleClickOpen = (row) => {
        const raw = row.value._raw || {};
        setEditRow(raw);
        setForm({
            name: raw.name || '',
            base_url: raw.base_url || '',
            is_enabled: !!raw.is_enabled,
            credentials: {...(raw.credentials || {})},
            settings: {...(raw.settings || {})},
        });
        setOpen(true);
    };

    const handleInputChange = (event) => {
        const {name, value} = event.target;
        setForm({
            ...form,
            [name]: name === 'is_enabled' ? value === 'true' || value === true : value,
        });
    };

    const handleCredentialChange = (key, value) => {
        setForm({...form, credentials: {...form.credentials, [key]: value}});
    };

    const handleSettingChange = (key, value) => {
        setForm({...form, settings: {...form.settings, [key]: value}});
    };

    const handleSave = () => {
        const payload = {
            id: editRow.id,
            name: form.name,
            base_url: form.base_url,
            is_enabled: form.is_enabled,
            settings: form.settings,
            ChangedBy: localStorage.getItem('user_id'),
        };

        const credentials = Object.fromEntries(
            Object.entries(form.credentials).filter(([, value]) => value !== '' && value != null)
        );
        if (Object.keys(credentials).length) {
            payload.credentials = credentials;
        }

        updatePartnerConnection(payload, (response) => {
            const updated = response?.data;
            if (updated && onReload) {
                const visibleKeys = labelsToStats(keys);
                onReload(rows.map((item) => flattenConnection(
                    Number(item.key) === Number(updated.id) ? updated : item.value._raw,
                    visibleKeys
                )));
            }
            setOpen(false);
            setOpenSend(true);
        });
    };

    return (
        <>
            <TableContainer component={Paper} sx={{
                padding: 2,
                backgroundColor: 'white',
                borderRadius: '8px',
                border: '6px',
                paddingLeft: 0,
                paddingTop: 0,
                marginLeft: '0px',
                marginTop: '10px',
                boxShadow: '0 0 1px rgba(0, 0, 0, 0.2)',
            }}>
                <Table>
                    <TableHead sx={{backgroundColor: '#f0f3f5'}}>
                        <TableRow>
                            {keys.map((key) => (
                                <TableCell key={key}>{key}</TableCell>
                            ))}
                            <TableCell key="edit-header"></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row) => (
                            <TableRow key={row.key}>
                                {keys.map((key) => (
                                    <TableCell key={key}>
                                        {row.value[key] === 'active' ? (
                                            <Button variant="contained" color="success" size="small">active</Button>
                                        ) : row.value[key] === 'blocked' ? (
                                            <Button variant="contained" color="error" size="small">disabled</Button>
                                        ) : (
                                            row.value[key]
                                        )}
                                    </TableCell>
                                ))}
                                <TableCell>
                                    <Button
                                        variant="outlined"
                                        startIcon={<EditIcon/>}
                                        color="primary"
                                        size="medium"
                                        onClick={() => handleClickOpen(row)}
                                    >
                                        Edit
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={openSend} onClose={() => setOpenSend(false)}>
                <Alert severity="success">
                    <AlertTitle>Success</AlertTitle>
                    Connection changed <strong>successfully!</strong>
                </Alert>
            </Dialog>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit connection {editRow?.name}</DialogTitle>
                <DialogContent>
                    <TextField margin="dense" name="name" label="Connection name" fullWidth value={form.name} onChange={handleInputChange}/>
                    <TextField margin="dense" name="base_url" label="Base URL" fullWidth value={form.base_url} onChange={handleInputChange}/>
                    <FormControl fullWidth margin="dense">
                        <InputLabel>Status</InputLabel>
                        <Select name="is_enabled" value={String(form.is_enabled)} label="Status" onChange={handleInputChange}>
                            <MenuItem value="true">Active</MenuItem>
                            <MenuItem value="false">Disabled</MenuItem>
                        </Select>
                    </FormControl>
                    {(credentialFieldsFor(editRow)).map((field) => (
                        <TextField
                            key={field.key}
                            margin="dense"
                            label={field.label || field.key}
                            type="text"
                            fullWidth
                            value={form.credentials[field.key] || ''}
                            onChange={(event) => handleCredentialChange(field.key, event.target.value)}
                        />
                    ))}
                    {(editRow?.partner?.setting_fields || []).map((field) => (
                        <TextField
                            key={field.key}
                            margin="dense"
                            label={field.label || field.key}
                            fullWidth
                            value={form.settings[field.key] || ''}
                            onChange={(event) => handleSettingChange(field.key, event.target.value)}
                        />
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={handleSave}>Save</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ApiConnectionsTable;
